<template>
  <div class="form-step">
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Projects ({{ localForm.projects.length }})</h5>
      <button type="button" class="btn btn-outline-primary" @click="addProject">
        <i class="bi bi-plus me-2"></i>Add Project
      </button>
    </div>

    <div v-if="projectDraft" class="project-item mb-4 p-3 border rounded bg-light-subtle">
      <div class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Title</label>
          <input ref="newProjectTitleInput" type="text" class="form-control" :class="{ 'field-invalid': isProjectFieldInvalid(projectDraft, 'title', null, true) }" v-model="projectDraft.title" placeholder="Project Title" @input="handleProjectDraftInput">
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start date</label>
          <input type="date" class="form-control" :class="{ 'field-invalid': isProjectFieldInvalid(projectDraft, 'startDate', null, true) }" v-model="projectDraft.startDate" @input="handleProjectDraftInput">
        </div>
        <div class="col-md-6">
          <label class="form-label">End date</label>
          <input type="date" class="form-control" :class="{ 'field-invalid': isProjectFieldInvalid(projectDraft, 'endDate', null, true) }" v-model="projectDraft.endDate" @input="handleProjectDraftInput">
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Year</label>
          <input type="number" class="form-control" v-model.number="projectDraft.year" :min="1900" :max="2100" placeholder="2025">
        </div>
        <div class="col-md-4">
          <label class="form-label">Type</label>
          <input type="text" class="form-control" :class="{ 'field-invalid': isProjectFieldInvalid(projectDraft, 'type', null, true) }" v-model="projectDraft.type" placeholder="Research, Clinical, etc." @input="handleProjectDraftInput">
        </div>
        <div class="col-md-4">
          <label class="form-label">Code</label>
          <input type="text" class="form-control" :class="{ 'field-invalid': isProjectFieldInvalid(projectDraft, 'code', null, true) }" v-model="projectDraft.code" placeholder="PROJECT001" @input="handleProjectDraftInput">
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Grant</label>
          <select class="form-select" v-model="projectDraft.grant">
            <option value="">Select Grant</option>
            <option v-for="(grant, grantIndex) in availableGrants" :key="`draft-grant-${grantIndex}`" :value="grant.code || grant.title">
              {{ grant.title }} {{ grant.code ? `(${grant.code})` : '' }}
            </option>
          </select>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewProject">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewProject">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(project, index) in localForm.projects || []" :key="projectKey(project, index)" class="project-item mb-4 p-3 border rounded">
      <div
        class="project-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleProject(projectKey(project, index))"
        @keydown.enter.prevent="toggleProject(projectKey(project, index))"
        @keydown.space.prevent="toggleProject(projectKey(project, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ projectHeaderTitles[projectKey(project, index)] }}</h6>
        <i class="bi" :class="isProjectExpanded(projectKey(project, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isProjectFieldInvalid(project, 'title', index) }"
            v-model="project.title"
            placeholder="Project Title"
            @input="markProjectDirty(index)"
          >
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isProjectFieldInvalid(project, 'startDate', index) }"
            v-model="project.startDate"
            @input="markProjectDirty(index)"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isProjectFieldInvalid(project, 'endDate', index) }"
            v-model="project.endDate"
            @input="markProjectDirty(index)"
          >
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">Collaborators</h6>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addCollaborator(index)">
          <i class="bi bi-plus me-2"></i>ADD
        </button>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" v-for="(collaborator, collabIndex) in project.collaborators || []" :key="collaborator._id" class="collaborator-item mb-3 p-2 border rounded bg-light">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">Collaborator {{ collabIndex + 1 }}</small>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeCollaborator(index, collabIndex)">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="row mb-2">
          <div class="col-md-4">
            <label class="form-label">Name</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="collaborator.department"
              @input="markProjectDirty(index)"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Institution</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="collaborator.institution"
              @input="markProjectDirty(index)"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label>
            <select
              class="form-select form-select-sm"
              v-model="collaborator.country"
              @change="markProjectDirty(index)"
            >
              <option value="">Select Country</option>
              <option v-for="country in internationalCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">Sites</h6>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addSite(index)">
          <i class="bi bi-plus me-2"></i>ADD
        </button>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" v-for="(site, siteIndex) in project.sites || []" :key="site._id" class="site-item mb-3 p-2 border rounded bg-light">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">Site {{ siteIndex + 1 }}</small>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSite(index, siteIndex)">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <label class="form-label">Name</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="site.name"
              placeholder="Site name"
              @input="markProjectDirty(index)"
            >
          </div>
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Subjects</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="project.subjects"
            placeholder="Describe the subjects or participants"
            @input="markProjectDirty(index)"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Outcomes</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="project.outcomes"
            placeholder="Describe the project outcomes"
            @input="markProjectDirty(index)"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Objectives</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="project.objectives"
            placeholder="Project objectives"
            @input="markProjectDirty(index)"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Year</label>
          <input
            type="number"
            class="form-control"
            v-model.number="project.year"
            :min="1900"
            :max="2100"
            placeholder="2025"
            @input="markProjectDirty(index)"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Type</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isProjectFieldInvalid(project, 'type', index) }"
            v-model="project.type"
            placeholder="Research, Clinical, etc."
            @input="markProjectDirty(index)"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Code</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isProjectFieldInvalid(project, 'code', index) }"
            v-model="project.code"
            placeholder="PROJECT001"
            @input="markProjectDirty(index)"
          >
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Grant</label>
          <select
            class="form-select"
            v-model="project.grant"
            @change="markProjectDirty(index)"
          >
            <option value="">Select Grant</option>
            <option
              v-for="(grant, grantIndex) in availableGrants"
              :key="`grant-${grantIndex}`"
              :value="grant.code || grant.title"
            >
              {{ grant.title }} {{ grant.code ? `(${grant.code})` : '' }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!projectDirty[index]" @click="saveProject(index)">
            <i class="bi bi-check me-1"></i>Save
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeProject(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Show message when no projects -->
    <div v-if="localForm.projects.length === 0" class="text-center py-4 text-muted">
      <i class="bi bi-folder2 fs-1 mb-3"></i>
      <p>No projects added yet. Click the ADD button to add your first project.</p>
    </div>
  </div>
</template>

<script>
import { parseDate } from '@/utils/dateUtils'
import { normalizeCountryValueToCode } from '@/utils/countryMap'
import ProfileApiService from '@/services/profile/ProfileApiService'

export default {
  name: 'ProjectsForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update', 'pending-state-change'],
  data() {
    const internationalCountryCodes = 'AF,AX,AL,DZ,AS,AD,AO,AI,AQ,AG,AR,AM,AW,AU,AT,AZ,BS,BH,BD,BB,BY,BE,BZ,BJ,BM,BT,BO,BQ,BA,BW,BV,BR,IO,BN,BG,BF,BI,CV,KH,CM,CA,KY,CF,CD,CK,CR,CI,HR,CU,CW,CY,CZ,DK,DJ,DM,DO,EC,EG,SV,GQ,ER,EE,SZ,ET,FK,FO,FJ,FI,FR,GF,PF,TF,GA,GM,GE,DE,GH,GI,GR,GL,GD,GP,GU,GT,GG,GN,GW,GY,HT,HM,VA,HN,HK,HU,IS,IN,ID,IR,IQ,IE,IM,IL,IT,JM,JP,JE,JO,KZ,KE,KI,KP,KR,KW,KG,LA,LV,LB,LS,LR,LY,LI,LT,LU,MO,MG,MW,MY,MV,ML,MT,MH,MQ,MR,MU,YT,MX,FM,MD,MC,MN,ME,MS,MA,MZ,MM,NA,NR,NP,NL,NC,NZ,NI,NE,NG,NU,NF,MK,MP,NO,OM,PK,PW,PS,PA,PG,PY,PE,PH,PN,PL,PT,PR,QA,RE,RO,RU,RW,BL,SH,KN,LC,MF,PM,VC,WS,SM,ST,SA,SN,RS,SC,SL,SG,SX,SK,SI,SB,SO,ZA,GS,SS,ES,LK,SD,SR,SJ,SE,CH,SY,TW,TJ,TZ,TH,TL,TG,TK,TO,TT,TN,TR,TM,TC,TV,UG,UA,AE,GB,UM,US,UY,UZ,VU,VE,VN,VG,VI,WF,EH,YE,ZM,ZW'.split(',')
    const regionNames = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
      ? new Intl.DisplayNames(['en'], { type: 'region' })
      : null
    const internationalCountries = internationalCountryCodes
      .map((value) => ({ value, label: regionNames?.of(value) || value }))
      .sort((left, right) => left.label.localeCompare(right.label))

    const africanCountries = [
      { value: 'DZ', label: 'Algeria' },
      { value: 'AO', label: 'Angola' },
      { value: 'BJ', label: 'Benin' },
      { value: 'BW', label: 'Botswana' },
      { value: 'BF', label: 'Burkina Faso' },
      { value: 'BI', label: 'Burundi' },
      { value: 'CM', label: 'Cameroon' },
      { value: 'CV', label: 'Cape Verde' },
      { value: 'CF', label: 'Central African Republic' },
      { value: 'TD', label: 'Chad' },
      { value: 'KM', label: 'Comoros' },
      { value: 'CG', label: 'Congo' },
      { value: 'CD', label: 'Democratic Republic of Congo' },
      { value: 'DJ', label: 'Djibouti' },
      { value: 'EG', label: 'Egypt' },
      { value: 'GQ', label: 'Equatorial Guinea' },
      { value: 'ER', label: 'Eritrea' },
      { value: 'SZ', label: 'Eswatini' },
      { value: 'ET', label: 'Ethiopia' },
      { value: 'GA', label: 'Gabon' },
      { value: 'GM', label: 'Gambia' },
      { value: 'GH', label: 'Ghana' },
      { value: 'GN', label: 'Guinea' },
      { value: 'GW', label: 'Guinea-Bissau' },
      { value: 'CI', label: 'Ivory Coast' },
      { value: 'KE', label: 'Kenya' },
      { value: 'LS', label: 'Lesotho' },
      { value: 'LR', label: 'Liberia' },
      { value: 'LY', label: 'Libya' },
      { value: 'MG', label: 'Madagascar' },
      { value: 'MW', label: 'Malawi' },
      { value: 'ML', label: 'Mali' },
      { value: 'MR', label: 'Mauritania' },
      { value: 'MU', label: 'Mauritius' },
      { value: 'MA', label: 'Morocco' },
      { value: 'MZ', label: 'Mozambique' },
      { value: 'NA', label: 'Namibia' },
      { value: 'NE', label: 'Niger' },
      { value: 'NG', label: 'Nigeria' },
      { value: 'RW', label: 'Rwanda' },
      { value: 'ST', label: 'São Tomé and Príncipe' },
      { value: 'SN', label: 'Senegal' },
      { value: 'SC', label: 'Seychelles' },
      { value: 'SL', label: 'Sierra Leone' },
      { value: 'SO', label: 'Somalia' },
      { value: 'ZA', label: 'South Africa' },
      { value: 'SS', label: 'South Sudan' },
      { value: 'SD', label: 'Sudan' },
      { value: 'TZ', label: 'Tanzania' },
      { value: 'TG', label: 'Togo' },
      { value: 'TN', label: 'Tunisia' },
      { value: 'UG', label: 'Uganda' },
      { value: 'ZM', label: 'Zambia' },
      { value: 'ZW', label: 'Zimbabwe' }
    ]

    return {
      localForm: { projects: [] },
      expandedProjectIds: [],
      projectDraft: null,
      projectDraftAttempted: false,
      projectDirty: {},
      projectAttempted: {},
      newProjectTitleInput: null,
      initialized: false,
      showDebug: false,
      internationalCountries,
      africanCountries
    }
  },
  computed: {
    projectHeaderTitles() {
      return (this.localForm.projects || []).reduce((titles, project, index) => {
        const key = this.projectKey(project, index)
        const code = this.normalizeHeaderText(project?.code).toUpperCase()
        const title = this.normalizeHeaderText(project?.title)
        const year = this.getYear(project?.year || project?.startDate)

        if (!code && !title && !year) {
          titles[key] = `New Project ${index + 1}`
          return titles
        }

        titles[key] = [code, title, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    },
    fellowDetailsProjects() {
      const projects = this.profileData?.data?.user?.fellow_details?.current_projects
      try {
        if (typeof projects === 'string') {
          const parsed = JSON.parse(projects)
          return Array.isArray(parsed) ? parsed : []
        } else if (Array.isArray(projects)) {
          return projects
        }
      } catch (e) {
        console.warn('Error parsing projects:', e)
      }
      return []
    },
    availableGrants() {
      // First try to get from formData (current session)
      if (this.formData?.grants && Array.isArray(this.formData.grants)) {
        return this.formData.grants.filter(grant => grant.title || grant.code)
      }

      // Fallback to profileData (saved grants)
      const fellowDetailsGrants = this.profileData?.data?.grants
      if (Array.isArray(fellowDetailsGrants) && fellowDetailsGrants.length > 0) {
        return fellowDetailsGrants.filter(grant => grant.title || grant.code)
      }

      return []
    },
    hasPendingChanges() {
      return Boolean(this.projectDraft)
    }
  },
  watch: {
    formData: {
      handler(newData) {
        if (!newData || this.initialized) return

        if (newData.projects?.length) {
          Object.assign(this.localForm, this.initializeLocalForm(newData))
          this.initializeExpandedProjectIds()
          this.initialized = true
        }
      },
      deep: false
    },

    // ⭐ ADD THIS
    profileData: {
      handler(newData) {
        if (!newData || this.initialized) return

        const dbProjects = newData?.data?.user?.projects

        if (dbProjects?.length) {
          const projects = this.mapDatabaseProjects(dbProjects)

          console.log("PROJECTS FROM PROFILE:", projects)

          Object.assign(this.localForm, this.initializeLocalForm({ projects }))
          this.initializeExpandedProjectIds()

          this.initialized = true // ⭐ lock initialization
        }
      },
      immediate: true
    },
    hasPendingChanges: {
      handler(isPending) {
        this.$emit('pending-state-change', isPending)
      },
      immediate: true
    }
  },
  methods: {
    makeId(prefix = '') {
      const now = Number(Date.now()); // ensure it’s a number
      const random = Math.floor(Math.random() * 10000);
      return `${prefix}${now.toString(36)}-${random}`;
    },


    initializeLocalForm(data = null) {
      const sourceData = data || this.formData || {};
      const projectsSource = Array.isArray(sourceData.projects) ? sourceData.projects : [];

      return {
        projects: projectsSource.map(project => {
          const normalizedCollaborators = this.normalizeCollaborators(project.collaborators)
          // Handle both snake_case and camelCase, and parse dates
          const startDateValue = project.start_date || project.startDate || ''
          const endDateValue = project.end_date || project.endDate || ''

          return {
            _id: project._id || this.makeId('proj-'),
            _isNew: false,
            title: project.title || '',
            startDate: parseDate(startDateValue, false),
            endDate: parseDate(endDateValue, true),
            collaborators: normalizedCollaborators,
            sites: (Array.isArray(project.sites) ? project.sites : []).map(s => ({
              _id: s._id || this.makeId('site-'),
              name: s.name || ''
            })),
            subjects: project.subjects || '',
            outcomes: project.outcomes || '',
            objectives: project.objectives || '',
            year: project.year || new Date().getFullYear(),
            type: project.type || '',
            code: project.code || '',
            grant: project.grant || ''
          }
        })
      }
    },

    normalizeCollaborators(collaborators) {
      try {
        if (!collaborators) return []

        if (typeof collaborators === 'string') {
          collaborators = JSON.parse(collaborators)
        }

        if (!Array.isArray(collaborators)) return []

        return collaborators.map(c => ({
          _id: c?._id || this.makeId('coll-'),
          department: c?.department || c?.name || '',
          institution: c?.institution || c?.Title || '',
          country: normalizeCountryValueToCode(c?.country || '', [this.internationalCountries, this.africanCountries])
        }))
      } catch (e) {
        console.warn('Error parsing collaborators', e)
        return []
      }
    },

    updateParent() {
      this.$emit('update', {
        ...this.localForm,
        projects: (this.localForm.projects || []).map(({ _isNew, ...project }) => ({ ...project }))
      })
    },

    addProject() {
      if (this.projectDraft) {
        this.$nextTick(() => {
          this.$refs.newProjectTitleInput?.focus()
        })
        return
      }

      this.projectDraft = {
        _id: this.makeId('proj-'),
        _isNew: true,
        title: '',
        startDate: '',
        endDate: '',
        collaborators: [],
        sites: [],
        subjects: '',
        outcomes: '',
        objectives: '',
        year: new Date().getFullYear(),
        type: '',
        code: '',
        grant: ''
      };
      this.projectDraftAttempted = false

      this.$nextTick(() => {
        this.$refs.newProjectTitleInput?.focus()
      })
    },

    cancelNewProject() {
      this.projectDraft = null
      this.projectDraftAttempted = false
    },

    isProjectFieldInvalid(project, field, index = null, isDraft = false) {
      const shouldValidate = isDraft ? this.projectDraftAttempted : Boolean(this.projectAttempted[index])
      if (!shouldValidate) return false

      switch (field) {
        case 'title':
          return !String(project?.title || '').trim()
        case 'startDate':
          return !String(project?.startDate || '').trim()
        case 'endDate':
          return !String(project?.endDate || '').trim() || this.hasInvalidDateRange(project)
        case 'type':
          return !String(project?.type || '').trim()
        case 'code':
          return !String(project?.code || '').trim()
        default:
          return false
      }
    },

    hasInvalidDateRange(project) {
      if (!project?.startDate || !project?.endDate) return false
      return new Date(project.startDate) > new Date(project.endDate)
    },

    handleProjectDraftInput() {
      if (this.projectDraftAttempted && this.isProjectValid(this.projectDraft)) {
        this.projectDraftAttempted = false
      }
    },

    isProjectValid(project) {
      if (!project) return false

      return Boolean(
        String(project.title || '').trim() &&
        String(project.startDate || '').trim() &&
        String(project.endDate || '').trim() &&
        String(project.type || '').trim() &&
        String(project.code || '').trim() &&
        !this.hasInvalidDateRange(project)
      )
    },

    async saveNewProject() {
      if (!this.isProjectValid(this.projectDraft)) {
        this.projectDraftAttempted = true
        if (this.hasInvalidDateRange(this.projectDraft)) {
          this.$emit('error', 'Project end date must be the same as or after the start date.')
        }
        return
      }

      try {
        this.localForm.projects.unshift({ ...this.projectDraft })
        const projectsData = { projects: this.localForm.projects }
        const response = await ProfileApiService.updateProfile(projectsData)

        // Refresh form data with latest from API response
        if (response?.data?.projects) {
          this.localForm.projects = response.data.projects
        }

        const key = this.projectKey(this.localForm.projects[0], 0)
        if (key && !this.expandedProjectIds.includes(key)) {
          this.expandedProjectIds.push(key)
        }
        this.projectDraft = null
        this.projectDraftAttempted = false
        this.updateParent()
      } catch (error) {
        console.error('Error saving new project:', error)
        this.$emit('error', 'Failed to save project. Please try again.')
        this.localForm.projects.shift()
      }
    },

    markProjectDirty(index) {
      this.projectDirty = {
        ...this.projectDirty,
        [index]: true
      }

      if (this.projectAttempted[index] && this.isProjectValid(this.localForm.projects[index])) {
        this.projectAttempted = {
          ...this.projectAttempted,
          [index]: false
        }
      }
    },

    async saveProject(index) {
      if (!this.isProjectValid(this.localForm.projects[index])) {
        this.projectAttempted = {
          ...this.projectAttempted,
          [index]: true
        }
        if (this.hasInvalidDateRange(this.localForm.projects[index])) {
          this.$emit('error', 'Project end date must be the same as or after the start date.')
        }
        return
      }

      try {
        const projectsData = { projects: this.localForm.projects }
        const response = await ProfileApiService.updateProfile(projectsData)

        // Refresh form data with latest from API response
        if (response?.data?.projects) {
          this.localForm.projects = response.data.projects
        }

        this.projectDirty = {
          ...this.projectDirty,
          [index]: false
        }
        this.projectAttempted = {
          ...this.projectAttempted,
          [index]: false
        }
        this.updateParent()
      } catch (error) {
        console.error('Error saving project:', error)
        this.$emit('error', 'Failed to save project. Please try again.')
      }
    },

    removeProject(index) {
      const removed = this.localForm.projects[index]
      this.localForm.projects.splice(index, 1)
      const removedKey = this.projectKey(removed, index)
      this.expandedProjectIds = this.expandedProjectIds.filter((id) => id !== removedKey)
      const nextDirty = {}
      Object.keys(this.projectDirty).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = this.projectDirty[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = this.projectDirty[key]
        }
      })
      this.projectDirty = nextDirty
      const nextAttempted = {}
      Object.keys(this.projectAttempted).forEach((key) => {
        const attemptedIndex = Number(key)
        if (attemptedIndex < index) {
          nextAttempted[attemptedIndex] = this.projectAttempted[key]
        }
        if (attemptedIndex > index) {
          nextAttempted[attemptedIndex - 1] = this.projectAttempted[key]
        }
      })
      this.projectAttempted = nextAttempted
      this.updateParent()
    },

    projectKey(project, index) {
      return project?._id || `project-${index}`
    },

    toggleProject(key) {
      if (!key) return

      if (this.expandedProjectIds.includes(key)) {
        this.expandedProjectIds = this.expandedProjectIds.filter((id) => id !== key)
      } else {
        this.expandedProjectIds.push(key)
      }
    },

    isProjectExpanded(key) {
      return this.expandedProjectIds.includes(key)
    },

    initializeExpandedProjectIds() {
      this.expandedProjectIds = (this.localForm.projects || [])
        .map((project, index) => (project._isNew ? this.projectKey(project, index) : null))
        .filter(Boolean)
    },

    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    getYear(value) {
      if (value === null || value === undefined) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    },

    addCollaborator(projectIndex) {
      this.localForm.projects[projectIndex].collaborators.push({
        _id: this.makeId('coll-'),
        department: '',
        institution: '',
        country: ''
      });
      this.markProjectDirty(projectIndex)
    },

    removeCollaborator(projectIndex, collabIndex) {
      this.localForm.projects[projectIndex].collaborators.splice(collabIndex, 1)
      this.markProjectDirty(projectIndex)
    },

    addSite(projectIndex) {
      this.localForm.projects[projectIndex].sites.push({
        _id: this.makeId('site-'),
        name: ''
      });
      this.markProjectDirty(projectIndex)
    },

    removeSite(projectIndex, siteIndex) {
      this.localForm.projects[projectIndex].sites.splice(siteIndex, 1)
      this.markProjectDirty(projectIndex)
    },

    mapDatabaseProjects(projects) {
      return projects.map(p => ({
        _id: p.id || this.makeId('proj-'),
        title: p.project_title || '',
        startDate: parseDate(p.start_date || p.startDate || '', false),
        endDate: parseDate(p.end_date || p.endDate || '', true),

        collaborators: this.normalizeCollaborators(p.collaborators),

        sites: this.normalizeSites(p.sites),

        subjects: p.subjects || '',
        outcomes: p.outcomes || '',
        objectives: p.objectives || '',
        year: p.year || new Date().getFullYear(),
        type: p.type || '',
        code: p.code || '',
        grant: p.grant_id || ''
      }))
    },

    normalizeSites(sites) {
      try {
        if (!sites) return []

        if (typeof sites === 'string') {
          sites = JSON.parse(sites)
        }

        if (!Array.isArray(sites)) return []

        return sites.map(s => ({
          _id: s?._id || this.makeId('site-'),
          name: typeof s === 'string' ? s : s?.name || ''
        }))
      } catch (e) {
        return []
      }
    }
  },

  mounted() {
    if (this.profileData?.data?.user?.projects?.length) {
      const projects = this.mapDatabaseProjects(
        this.profileData.data.user.projects
      )

      Object.assign(this.localForm, this.initializeLocalForm({ projects }))
      this.initializeExpandedProjectIds()
    }

    this.initializeExpandedProjectIds()
  }

}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}
.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
}
.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}
.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}
.project-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}
.collaborator-item,
.site-item {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
}
h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-base);
}
.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}
.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}
.form-control-sm,
.form-select-sm {
  padding: 0.375rem 0.75rem;
  font-size: var(--font-size-sm);
}
.text-muted {
  font-size: var(--font-size-sm);
}

.fs-1 {
  font-size: var(--font-size-4xl) !important;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
